import type {
  HandleResolutionSetupChange,
  ResolutionInputTypeComboboxOption,
  ResolutionSetup,
  ResolutionTypeComboboxOption
} from '@library/types/resolutions/resolutionDetails';
import {
  resolutionInputTypeMapping,
  resolutionInputTypeOptions,
  resolutionTypeOptions
} from '@bo-constants/resolutions/resolutionDetails';
import { useCallback, useMemo } from 'react';
import type { ComboboxOption } from '@library/components/selects';

export const useResolutionDetailsSetup = ({
  resolutionSetup,
  onChangeResolutionSetup
}: {
  resolutionSetup: ResolutionSetup;
  onChangeResolutionSetup: HandleResolutionSetupChange;
}) => {
  const { resolutionInputType, resolutionType } = resolutionSetup || {};

  const selectedResolutionTypeOption = useMemo(() => {
    return resolutionTypeOptions.find(
      (resolutionOption) => resolutionOption.value === resolutionType
    );
  }, [resolutionType]);

  const selectedResolutionInputTypeOption = useMemo(() => {
    return resolutionInputTypeOptions.find(
      (resolutionInputTypeOption) =>
        resolutionInputTypeOption.value === resolutionInputType
    );
  }, [resolutionInputType]);

  const filteredResolutionInputTypeOptions = useMemo(() => {
    const validResolutionInputTypes = resolutionType
      ? resolutionInputTypeMapping[resolutionType]
      : [];

    return resolutionInputTypeOptions.filter((resolutionInputTypeOption) =>
      validResolutionInputTypes.includes(resolutionInputTypeOption.value)
    );
  }, [resolutionType]);

  const handleResolutionTypeChange = useCallback(
    (option: ComboboxOption | null) => {
      const newResolutionTypeOption = option as ResolutionTypeComboboxOption;

      onChangeResolutionSetup({
        key: 'resolutionType',
        value: newResolutionTypeOption?.value
      });
    },
    [onChangeResolutionSetup]
  );

  const handleResolutionInputTypeChange = useCallback(
    (option: ComboboxOption | null) => {
      const newResolutionInputTypeOption =
        option as ResolutionInputTypeComboboxOption;

      onChangeResolutionSetup({
        key: 'resolutionInputType',
        value: newResolutionInputTypeOption?.value
      });
    },
    [onChangeResolutionSetup]
  );

  return {
    onChangeResolutionInputType: handleResolutionInputTypeChange,
    onChangeResolutionType: handleResolutionTypeChange,
    resolutionTypeOptions,
    resolutionInputTypeOptions: filteredResolutionInputTypeOptions,
    selectedResolutionInputTypeOption,
    selectedResolutionTypeOption
  };
};
